import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
} from 'date-fns';
import { ptBR, es, enUS } from 'date-fns/locale';

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a');
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true });
};

export function getRelativeTimeText(unixTimestamp, translator) {
  if (!unixTimestamp) return '-';

  const now = new Date();
  const past = fromUnixTime(unixTimestamp);
  if (Number.isNaN(past.getTime())) return '-';

  const diffMs = now - past;

  const seconds = Math.floor(diffMs / 1000) % 60;
  const minutes = Math.floor(diffMs / (1000 * 60)) % 60;
  const hours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const parts = [];

  if (days > 0) {
    parts.push(
      translator(
        'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RELATIVE_TIME.DAYS',
        days,
        {
          count: days,
        }
      )
    );
  }
  if (hours > 0 && parts.length < 2) {
    parts.push(
      translator(
        'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RELATIVE_TIME.HOURS',
        hours,
        {
          count: hours,
        }
      )
    );
  }
  if (minutes > 0 && parts.length < 2) {
    parts.push(
      translator(
        'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RELATIVE_TIME.MINUTES',
        minutes,
        {
          count: minutes,
        }
      )
    );
  }
  if (seconds > 0 && parts.length === 0) {
    parts.push(
      translator('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.RELATIVE_TIME.JUST_NOW')
    );
  }

  return parts.join(' • ');
}

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Gets the appropriate date-fns locale based on the current i18n locale
 * @param {Object} i18n - Vue i18n instance (this.$i18n)
 * @returns {Object} date-fns locale object
 */
export function getCurrentLocale(i18n) {
  const currentLanguage = i18n.locale || 'pt_BR';

  switch (currentLanguage) {
    case 'pt_BR':
      return ptBR;
    case 'es':
      return es;
    case 'en':
    default:
      return enUS;
  }
}

/**
 * Gets SLA status text with localized time formatting
 * @param {number|null} timestamp - Unix timestamp
 * @param {Object} i18n - Vue i18n instance (this.$i18n)
 * @param {Function} translator - Translation function (this.$t)
 * @returns {string} Localized SLA status text
 */
export function getSlaStatusText(timestamp, i18n, translator) {
  if (!timestamp) return translator('CONVERSATION.SLA.STATUS.NOT_SET');

  const now = Date.now();
  const slaTime = timestamp * 1000;
  const isOverdue = now > slaTime;
  const currentLocale = getCurrentLocale(i18n);

  const timeText = formatDistanceToNow(new Date(slaTime), {
    addSuffix: false,
    locale: currentLocale,
  });

  if (isOverdue) {
    return translator('CONVERSATION.SLA.STATUS.OVERDUE', {
      time: timeText,
    });
  }
  return translator('CONVERSATION.SLA.STATUS.DUE_IN', {
    time: timeText,
  });
}
